/**
 * Global request deduplication and caching utility
 * Prevents multiple identical API requests from being made simultaneously
 */

interface CachedRequest<T> {
  promise: Promise<T>;
  timestamp: number;
  data?: T;
}

class RequestCache {
  private cache = new Map<string, CachedRequest<any>>();
  private readonly TTL = 30000; // 30 seconds cache TTL

  /**
   * Get or create a cached request
   * @param key Unique identifier for the request
   * @param requestFn Function that returns a promise for the actual request
   * @returns Promise that resolves to the cached or fresh data
   */
  async getOrFetch<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const cached = this.cache.get(key);

    // Check if we have a valid cached entry
    if (cached && (now - cached.timestamp) < this.TTL) {
      console.log(`[RequestCache] Using cached request for key: ${key}`);
      
      // If we have cached data, return it immediately
      if (cached.data !== undefined) {
        return cached.data;
      }
      
      // If request is in flight, wait for it
      return cached.promise;
    }

    // Create new request
    console.log(`[RequestCache] Creating new request for key: ${key}`);
    
    const promise = requestFn().then(data => {
      // Store the resolved data
      const entry = this.cache.get(key);
      if (entry) {
        entry.data = data;
      }
      return data;
    }).catch(error => {
      // Remove failed requests from cache
      this.cache.delete(key);
      throw error;
    });

    // Cache the promise
    this.cache.set(key, {
      promise,
      timestamp: now
    });

    return promise;
  }

  /**
   * Invalidate a specific cache entry
   */
  invalidate(key: string): void {
    console.log(`[RequestCache] Invalidating cache for key: ${key}`);
    this.cache.delete(key);
  }

  /**
   * Clear all cached requests
   */
  clear(): void {
    console.log('[RequestCache] Clearing all cached requests');
    this.cache.clear();
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.TTL) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      console.log(`[RequestCache] Cleaned up ${removedCount} expired entries`);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Global instance
export const requestCache = new RequestCache();

// Auto-cleanup every 5 minutes
setInterval(() => {
  requestCache.cleanup();
}, 5 * 60 * 1000);

/**
 * Helper function to create cache keys for SMS usage requests
 */
export const createSmsUsageCacheKey = (
  organisationId: string,
  restaurantIds: string[],
  startDate: string,
  endDate: string
): string => {
  const sortedRestaurantIds = restaurantIds.sort().join(',');
  return `sms-usage:${organisationId}:${sortedRestaurantIds}:${startDate}:${endDate}`;
};

/**
 * Helper function to create cache keys for restaurant list requests
 */
export const createRestaurantListCacheKey = (organisationId: string): string => {
  return `restaurant-list:${organisationId}`;
};